import * as React from "react";
import * as Immutable from "immutable";
import "./Comment.styl";
import CommentModel from "../models/CommentModel";
import DiscussionContext from "./Context";
import CommentHead from "./CommentHead";
import CommentFoot from "./CommentFoot";
import RatingModel from "../models/RatingModel";
import CommentReply from "./CommentReply";
import UserModel from "../models/UserModel";
import DiscussionModel from "../models/DiscussionModel";

export default class Comment extends React.PureComponent<{
    model: DiscussionModel;
    comment: CommentModel;
    rating: RatingModel | null;
    user: UserModel | null;
    authors: Immutable.Set<number>;
    admin: boolean;
    badges: boolean;
}> {

    private reply: React.RefObject<HTMLDivElement> = React.createRef();
    private timer: number = 0;

    public render(): React.ReactNode {
        const verificationTimestamp = (new Date(2021, 1, 25, 0, 0, 0)).getTime() / 1000;
        const comment = this.props.comment;
        const model = this.props.model;
        if (comment.isBlocked()) {
            return (
                <div className="Discussion-Comment-Root">
                    {this.renderBlocked()}
                </div>
            );
        } else if (comment.getUser() && !comment.getUser().isVerified() && comment.getTimestamp() > verificationTimestamp) {
            if (this.props.admin) {
                return (
                    <div className="Discussion-Comment-Root">
                        {this.renderUnverifiedWithContent()}
                    </div>
                );
            } else {
                return (
                    <div className="Discussion-Comment-Root">
                        {this.renderUnverified()}
                    </div>
                );
            }
        } else {
            return (
                <div className="Discussion-Comment-Root">
                    {this.renderContent()}
                </div>
            );
        }
    }

    private renderUnverified(): React.ReactNode {
        return (
            <DiscussionContext.Consumer>
                {({translate}) => (
                    <React.Fragment>
                        <CommentHead
                            comment={this.props.comment}
                            user={this.props.user}
                            authors={this.props.authors}
                            admin={this.props.admin}
                            badges={this.props.badges}
                            report={false}
                        />
                        <div className="Discussion-Comment-Unverified">
                            <div className="Discussion-Comment-UnverifiedTitle">
                                {translate('UNVERIFIED_TITLE')}
                            </div>
                            <div className="Discussion-Comment-UnverifiedText">
                                {translate('UNVERIFIED_TEXT_1')}
                                {' '}
                                <a href={this.props.model.getVerify()}>{translate('UNVERIFIED_TEXT_2')}</a>
                                {' '}
                                {translate('UNVERIFIED_TEXT_3')}
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </DiscussionContext.Consumer>
        );
    }

    private renderUnverifiedWithContent(): React.ReactNode {
        return (
            <DiscussionContext.Consumer>
                {({translate}) => (
                    <React.Fragment>
                        <CommentHead
                            comment={this.props.comment}
                            user={this.props.user}
                            authors={this.props.authors}
                            admin={this.props.admin}
                            badges={this.props.badges}
                            report={false}
                        />
                        {this.renderBody()}
                        <div className="Discussion-Comment-Unverified">
                            <div className="Discussion-Comment-UnverifiedTitle">
                                {translate('UNVERIFIED_TITLE')}
                            </div>
                            <div className="Discussion-Comment-UnverifiedText">
                                {translate('UNVERIFIED_TEXT_1')}
                                {' '}
                                <a href={this.props.model.getVerify()}>{translate('UNVERIFIED_TEXT_2')}</a>
                                {' '}
                                {translate('UNVERIFIED_TEXT_3')}
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </DiscussionContext.Consumer>
        );
    }

    private renderContent(): React.ReactNode {
        const comment = this.props.comment;
        const rating = this.props.rating;
        return (
            <React.Fragment>
                <CommentHead
                    comment={comment}
                    user={this.props.user}
                    authors={this.props.authors}
                    admin={this.props.admin}
                    badges={this.props.badges}
                    report={true}
                />
                {this.renderBody()}
                {this.renderFoot()}
                <div className="Discussion-Comment-Reply" ref={this.reply}>
                    <div style={{height: "20px"}}/>
                    <CommentReply comment={comment}/>
                </div>
            </React.Fragment>
        );
    }

    private renderBody(): React.ReactNode {

        const comment = this.props.comment;

        let text = comment.getContent();
        let short = false;

        if (!comment.isExpanded() && text.length > 500) {
            text = text.substr(0, 450).replace(/\s[^\s]+$/, '') + "... ";
            short = true;
        }

        return (
            <DiscussionContext.Consumer>
                {({dispatch, translate}) => (
                    <div className="Discussion-Comment-Content">
                        {text}
                        <span className="Discussion-Comment-Extend" data-display={short ? "1" : "0"} onClick={() => {
                            dispatch('comment/expand', comment.getCommentId());
                        }}>
                            {translate("COMMENT_EXTEND")}
                        </span>
                    </div>
                )}
            </DiscussionContext.Consumer>
        );
    }

    private renderBlocked(): React.ReactNode {
        return (
            <DiscussionContext.Consumer>
                {({translate}) => (
                    <div className="Discussion-Comment-Blocked">
                        <div className="Discussion-Comment-BlockedIcon"/>
                        <div className="Discussion-Comment-BlockedLabel">
                            {translate("COMMENT_BLOCKED")}&nbsp;<a href="https://refresher.sk/info/komunitne-pravidla">{translate("COMMENT_BLOCKED_LINK")}</a>
                        </div>
                    </div>
                )}
            </DiscussionContext.Consumer>
        )
    }

    private renderFoot(): React.ReactNode {
        if (this.props.comment.isBlocked() == false) {
            return (
                <CommentFoot comment={this.props.comment} rating={this.props.rating}/>
            );
        }
        return null;
    }

    public componentDidUpdate(props: { comment: CommentModel }): void {

        const reply = this.reply.current;
        const display = this.props.comment.getReplyDisplay();

        if (props.comment.getReplyDisplay() != display) {

            if (this.timer) {
                window.clearTimeout(this.timer);
            }

            if (display) {

                if (!this.timer) {
                    reply.style.overflow = "hidden";
                    reply.style.height = "0";
                    reply.style.display = "block";
                }

                reply.style.transition = "height .5s ease-in";

                this.timer = window.setTimeout(() => {
                    reply.style.height = reply.scrollHeight + "px";
                    this.timer = window.setTimeout(() => {
                        reply.style.overflow = "none";
                        reply.style.height = "auto";
                        reply.style.transition = "";
                        this.timer = 0;
                    }, 500);
                }, 0);

            } else {

                if (!this.timer) {
                    reply.style.overflow = "hidden";
                    reply.style.height = reply.scrollHeight + "px";
                }

                reply.style.transition = "height .5s ease-out";

                this.timer = window.setTimeout(() => {
                    reply.style.height = "0";
                    this.timer = window.setTimeout(() => {
                        reply.style.transition = "";
                        reply.style.display = "none";
                        this.timer = 0;
                    }, 500);
                }, 0);

            }
        }

    }

}